export class ApiError extends Error {
  constructor(message, details = null, status = null) {
    super(message || 'The request could not be completed.');
    this.name = 'ApiError';
    this.details = details;
    this.status = status;
  }
}
export function unwrapEnvelope(value) {
  if (!value || typeof value.isSuccess !== 'boolean') {
    throw new ApiError('The server returned an invalid response.');
  }
  if (!value.isSuccess) {
    throw new ApiError(value.message || 'The request could not be completed.', value.data);
  }
  return value.data;
}

export function extractItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

export function getResponseMessage(responseLike, fallback = 'Request completed.') {
  if (!responseLike) return fallback;
  return responseLike.message || responseLike.error || fallback;
}

export function getErrorMessage(error, fallback = 'The request could not be completed.') {
  if (error?.response?.status === 409) {
    return error?.response?.data?.message || 'This record changed after you opened it. Refresh and try again.';
  }
  return error?.response?.data?.message || error?.message || fallback;
}

export function getValidationMessages(error) {
  const payload = error?.details ?? error?.response?.data?.data ?? error?.response?.data?.errors;
  if (Array.isArray(payload)) {
    return payload.map((item) => {
      if (typeof item === 'string') return item;
      const field = item?.field || item?.Field || '';
      const message = item?.error || item?.Error || item?.message || item?.Message || '';
      return message ? `${field ? `${field}: ` : ''}${message}` : '';
    }).filter(Boolean);
  }
  if (payload && typeof payload === 'object') {
    return Object.entries(payload).flatMap(([field, value]) => {
      const messages = Array.isArray(value) ? value : [value];
      return messages.filter(Boolean).map((message) => `${field}: ${message}`);
    });
  }
  return [];
}

export function downloadBlob(response, fileName) {
  const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function fileNameFromResponse(response, fallback = 'leave-document') {
  const header = response?.headers?.['content-disposition'] || response?.headers?.get?.('content-disposition');
  const encoded = header?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = header?.match(/filename="?([^";]+)"?/i)?.[1];
  let value = fallback;
  try {
    value = encoded ? decodeURIComponent(encoded) : plain || fallback;
  } catch (_) {
    value = fallback;
  }
  return String(value).replace(/[\\/:*?"<>|]/g, '_').replace(/^\.+/, '') || fallback;
}

export function openBlob(response) {
  const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data]);
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  return Boolean(opened);
}

export function toDateInput(value) {
  if (!value) return '';
  const calendarDate = String(value).match(/^(\d{4}-\d{2}-\d{2})(?:T|$)/);
  if (calendarDate) return calendarDate[1];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
}

export function toTimeInput(value = '09:00') {
  if (!value) return '09:00';
  return value.slice(0, 5);
}

export function toApiDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
}

export function toApiTime(value) {
  if (!value) return '09:00:00';
  return value.length === 5 ? `${value}:00` : value;
}
